// Save reconcile after a defs change (IMPLEMENTATION §2.9 "Save": a save written with other defs is LOADED
// ANYWAY; what the current defs do not know is dropped with a refund). Pure, integer-only sim code (arch-tested,
// ported with the save loader); save/SaveService.ts calls it on a defsHash mismatch, logs the telemetry row and
// then runs checkInvariants — a state this cannot repair is rejected like a corrupt save (→ .bak → fresh).
//   mask          an index outside the expansion table is clamped to the last mask
//   progress      unknown / inactive unlocks (and unlocks whose prerequisite went) dropped; unknown discoveries,
//                 shop ids and first-build types dropped; goal / tutorial indices clamped
//   buildings     unknown / inactive type or deposit ore, a cell outside the mask or already taken, a ghost
//                 holding an unknown item, or a shape the current defs reject after the repairs below
//                 (buildingErrors: slots, capacities, recipe, …) → removed; refund = `paid` + the value of
//                 everything it held
//   contents      a kept building's unknown items (chute carry, slots, out, a running cycle, Packer buffer,
//                 Gallery gun / queue, Dock queue, Crate tiers / family) are removed and refunded in place;
//                 levels above the level table are clamped; unknown stalls cleared; readout rings whose length
//                 changed restart empty
//   floor         unknown items, items outside the mask or on a taken cell → removed and refunded
//   rings         income rings whose length changed restart empty (display only)
//   ledger        rows for every material; units that can no longer be accounted for (a removed family, a new
//                 units table) are written off (`sold`, or `granted` when the world holds more) so it balances
// A refund is the explicit value table's entry for that family and tier (0 when the table has none) and goes to
// cash only (not lifetime income); the units of a refunded item are booked `sold`.
import { maxLevelOf } from './bdefs.ts';
import { cellIndex } from './core/grid.ts';
import type { Int } from './core/types.ts';
import { isActive, type SimDefs } from './defs.ts';
import { isValidItem } from './entities.ts';
import { type FamId, MATS, type Mat } from './gen/ids.ts';
import { buildingErrors } from './invariants.ts';
import { forEachStoredRef, inWorld, procOf } from './ledger.ts';
import { ringSlots, zeroRing } from './readout.ts';
import type { Building, GalleryWin, ItemRef, MatLedger, SimState } from './state.ts';

export interface DroppedEntry {
  kind:
    | 'item'
    | 'building'
    | 'content'
    | 'unlock'
    | 'discovery'
    | 'shop'
    | 'firstBuilt'
    | 'index'
    | 'ledger';
  what: string;
  refund: Int;
}

export interface ReconcileReport {
  dropped: DroppedEntry[];
  refund: Int;
}

interface Acc {
  s: SimState;
  d: SimDefs;
  dropped: DroppedEntry[];
  refund: Int;
}

function famOf(d: SimDefs, fam: string) {
  for (const f of d.families) if (f.id === fam) return f;
  return null;
}

function known(d: SimDefs, fam: string, tier: Int): boolean {
  return isValidItem(d, fam as FamId, tier);
}

/** Value of fam T`tier` from the explicit table (0 when the family or tier is not in it). */
function tierValue(d: SimDefs, fam: string, tier: Int): Int {
  return famOf(d, fam)?.values[tier - 1] ?? 0;
}

/** Refunds `n` items of fam T`tier` and books their units `sold` (units of an unknown family cannot be booked). */
function refundItems(a: Acc, fam: string, tier: Int, n: Int): Int {
  const v = tierValue(a.d, fam, tier) * n;
  const f = famOf(a.d, fam);
  const per = a.d.tierUnits[tier - 1];
  if (f !== null && per !== undefined) {
    for (const u of f.units) {
      const row = a.s.ledger[u.mat];
      if (row) row.sold += n * u.n * per;
    }
  }
  a.refund += v;
  return v;
}

/** Keeps the known refs of `xs`; the rest is refunded and reported as `where`. */
function scrubRefs<T extends ItemRef>(a: Acc, b: Building, xs: T[], where: string): T[] {
  const kept: T[] = [];
  for (const r of xs) {
    if (known(a.d, r.fam, r.tier)) kept.push(r);
    else {
      const v = refundItems(a, r.fam, r.tier, 1);
      a.dropped.push({
        kind: 'content',
        what: `${b.type} #${b.id} ${where}: ${r.fam} T${r.tier}`,
        refund: v,
      });
    }
  }
  return kept;
}

/** Refunds a whole building: `paid` plus everything it holds. */
function refundBuilding(a: Acc, b: Building, why: string): void {
  let v = b.paid;
  a.refund += b.paid;
  forEachStoredRef(b, (r) => {
    v += refundItems(a, r.fam, r.tier, 1);
  });
  if (b.type === 'crate' && b.fam !== null) {
    for (let t = 0; t < b.counts.length; t++) {
      const n = b.counts[t] ?? 0;
      if (n > 0) v += refundItems(a, b.fam, t + 1, n);
    }
  }
  const p = procOf(b);
  if (p !== null) v += refundItems(a, p.fam, p.tier, 1);
  a.dropped.push({
    kind: 'building',
    what: `${b.type}${b.type === 'deposit' ? `:${b.ore}` : ''} L${b.level} #${b.id} (${why})`,
    refund: v,
  });
}

function knownBuilding(d: SimDefs, b: Building): boolean {
  if (b.type === 'deposit') {
    for (const x of d.deposits) if (x.id === b.ore) return isActive(d, x.version);
    return false;
  }
  const def = (d.buildings as unknown as Record<string, { version: string } | undefined>)[b.type];
  return def !== undefined && isActive(d, def.version as SimDefs['contentVersion']);
}

function fitRing(ring: Int[], len: Int): boolean {
  return ring.length === len;
}

function freshWin(d: SimDefs): GalleryWin {
  return {
    ready: zeroRing(d),
    idle: zeroRing(d),
    full: zeroRing(d),
    fired: zeroRing(d),
    supplied: zeroRing(d),
    paid: zeroRing(d),
    idx: 0,
    n: 0,
  };
}

/** Repairs a kept building in place; false = it must go (refunded by the caller). */
function repairBuilding(a: Acc, b: Building): boolean {
  const d = a.d;
  if (b.ghost !== null) {
    for (const r of b.ghost.delivered) if (!known(d, r.fam, r.tier)) return false;
  }
  if (b.stall !== null && !d.stalls.some((x) => x.id === b.stall)) {
    b.stall = null;
    b.stallT = 0;
  }
  let max = 0;
  try {
    max = maxLevelOf(d, b);
  } catch {
    return false;
  }
  if (b.level > max && max >= 1) {
    a.dropped.push({
      kind: 'index',
      what: `${b.type} #${b.id} level ${b.level} → ${max}`,
      refund: 0,
    });
    b.level = max;
  }
  const ring = ringSlots(d);
  switch (b.type) {
    case 'chute':
      if (b.carry !== null && !known(d, b.carry.fam, b.carry.tier)) {
        scrubRefs(a, b, [b.carry], 'carry');
        b.carry = null;
        b.prog = 0;
      }
      break;
    case 'caster':
    case 'grinder':
    case 'smelter':
    case 'press':
    case 'loader':
    case 'gunsmith':
      b.slots = b.slots.map((slot, i) => scrubRefs(a, b, slot, `slot ${i}`));
      b.out = scrubRefs(a, b, b.out, 'out');
      if (b.proc !== null && !known(d, b.proc.fam, b.proc.tier)) {
        const v = refundItems(a, b.proc.fam, b.proc.tier, 1);
        a.dropped.push({
          kind: 'content',
          what: `${b.type} #${b.id} cycle: ${b.proc.fam} T${b.proc.tier}`,
          refund: v,
        });
        b.proc = null;
        b.prog = 0;
      }
      if (!fitRing(b.busy, ring)) b.busy = zeroRing(d);
      break;
    case 'packer':
      b.buf = scrubRefs(a, b, b.buf, 'buffer');
      b.out = scrubRefs(a, b, b.out, 'out');
      if (b.lockFam !== null && famOf(d, b.lockFam) === null && b.buf.length + b.out.length === 0)
        b.lockFam = null;
      if (!fitRing(b.busy, ring)) b.busy = zeroRing(d);
      break;
    case 'gallery': {
      if (b.gun !== null && !known(d, b.gun.fam, b.gun.tier)) {
        scrubRefs(a, b, [b.gun], 'gun');
        b.gun = null;
      }
      b.queue = scrubRefs(a, b, b.queue, 'queue');
      const w = b.win;
      if (
        !fitRing(w.ready, ring) ||
        !fitRing(w.idle, ring) ||
        !fitRing(w.full, ring) ||
        !fitRing(w.fired, ring) ||
        !fitRing(w.supplied, ring) ||
        !fitRing(w.paid, ring) ||
        w.idx < 0 ||
        w.idx >= ring
      )
        b.win = freshWin(d);
      break;
    }
    case 'dock':
      b.queue = scrubRefs(a, b, b.queue, 'queue');
      break;
    case 'crate': {
      const len = d.tierUnits.length;
      const fam = b.fam;
      const fk = fam !== null ? famOf(d, fam) : null;
      const famOk = fk !== null && isActive(d, fk.version);
      const counts: Int[] = [];
      for (let t = 0; t < len; t++) counts.push(0);
      for (let t = 0; t < b.counts.length; t++) {
        const n = b.counts[t] ?? 0;
        if (n <= 0) continue;
        if (fam !== null && famOk && t < len && known(d, fam, t + 1)) counts[t] = n;
        else if (fam !== null) {
          const v = refundItems(a, fam, t + 1, n);
          a.dropped.push({
            kind: 'content',
            what: `crate #${b.id}: ${n} × ${fam} T${t + 1}`,
            refund: v,
          });
        }
      }
      b.counts = counts;
      if (fam !== null && !famOk) b.fam = null;
      break;
    }
    default:
      break;
  }
  try {
    return buildingErrors(d, b).length === 0;
  } catch {
    return false;
  }
}

/** Drops what `d` does not know from `s` (in place) and refunds it to cash. */
export function reconcileWithDefs(s: SimState, d: SimDefs): ReconcileReport {
  const a: Acc = { s, d, dropped: [], refund: 0 };
  normaliseLedgerRows(a, Object.keys(s.ledger));

  // mask
  if (s.bounds < 0 || s.bounds >= d.board.expansions.length) {
    a.dropped.push({ kind: 'index', what: `bounds ${s.bounds}`, refund: 0 });
    s.bounds = s.bounds < 0 ? 0 : d.board.expansions.length - 1;
  }
  const m = d.board.expansions[s.bounds];
  const inMask = (x: Int, y: Int): boolean =>
    m !== undefined && x >= m.x0 && x <= m.x1 && y >= m.y0 && y <= m.y1;
  const taken: boolean[] = [];

  // buildings
  const kept: Building[] = [];
  for (const b of s.buildings) {
    let why = '';
    if (!knownBuilding(d, b)) why = 'unknown';
    else if (!inMask(b.x, b.y)) why = 'outside the mask';
    else if (taken[cellIndex(d.board.cols, b.x, b.y)] === true) why = 'cell taken';
    else if (!repairBuilding(a, b)) why = 'invalid';
    if (why !== '') {
      refundBuilding(a, b, why);
      continue;
    }
    taken[cellIndex(d.board.cols, b.x, b.y)] = true;
    kept.push(b);
  }
  s.buildings = kept;

  // loose items
  s.floor = s.floor.filter((it) => {
    let why = '';
    if (!known(d, it.fam, it.tier)) why = 'unknown';
    else if (!inMask(it.x, it.y)) why = 'outside the mask';
    else if (taken[cellIndex(d.board.cols, it.x, it.y)] === true) why = 'cell taken';
    if (why === '') {
      taken[cellIndex(d.board.cols, it.x, it.y)] = true;
      return true;
    }
    const v = refundItems(a, it.fam, it.tier, 1);
    a.dropped.push({ kind: 'item', what: `${it.fam} T${it.tier} #${it.id} (${why})`, refund: v });
    return false;
  });

  reconcileProgress(a);

  // income rings (display only): restart when the ring length changed
  const inc = s.income;
  const rb = d.economy.income.ringBuckets;
  if (inc.total.length !== rb || inc.auto.length !== rb || inc.idx < 0 || inc.idx >= rb) {
    a.dropped.push({ kind: 'index', what: 'income rings', refund: 0 });
    inc.total = [];
    inc.auto = [];
    for (let i = 0; i < rb; i++) {
      inc.total.push(0);
      inc.auto.push(0);
    }
    inc.idx = 0;
  }

  writeOffLedger(a);
  s.wallet.cash += a.refund;
  return { dropped: a.dropped, refund: a.refund };
}

function zeroLedgerRow(): MatLedger {
  return { mined: 0, granted: 0, sold: 0, burned: 0, fired: 0, consumedBuild: 0 };
}

/** A row per current material; rows of materials the defs no longer have are dropped. */
function normaliseLedgerRows(a: Acc, keys: readonly string[]): void {
  const rows = a.s.ledger as Record<string, MatLedger | undefined>;
  for (const k of keys) {
    if ((MATS as readonly string[]).includes(k)) continue;
    delete rows[k];
    a.dropped.push({ kind: 'ledger', what: `material ${k}`, refund: 0 });
  }
  for (const mat of MATS) if (rows[mat] === undefined) rows[mat] = zeroLedgerRow();
}

/** Books the units nobody can account for any more so that mined + granted = inWorld + sold + … again. */
function writeOffLedger(a: Acc): void {
  const world = inWorld(a.s, a.d);
  for (const mat of MATS) {
    const l = a.s.ledger[mat as Mat];
    const diff = l.mined + l.granted - (world[mat] + l.sold + l.burned + l.fired + l.consumedBuild);
    if (diff === 0) continue;
    if (diff > 0) l.sold += diff;
    else l.granted -= diff;
    a.dropped.push({
      kind: 'ledger',
      what: `${mat} ${diff > 0 ? '+' : ''}${diff} units`,
      refund: 0,
    });
  }
}

function reconcileProgress(a: Acc): void {
  const { s, d } = a;
  const p = s.progress;
  const unlocked: typeof p.unlocked = [];
  for (const id of p.unlocked) {
    const u = d.unlocks.find((x) => x.id === id);
    const ok =
      u !== undefined &&
      isActive(d, u.version) &&
      !unlocked.includes(id) &&
      (u.after === null || unlocked.includes(u.after));
    if (ok) unlocked.push(id);
    else a.dropped.push({ kind: 'unlock', what: id, refund: 0 });
  }
  p.unlocked = unlocked;
  p.discovered = p.discovered.filter((key) => {
    const i = key.indexOf(':');
    const fam = i < 0 ? '' : key.slice(0, i);
    const tier = i < 0 ? 0 : Number.parseInt(key.slice(i + 1), 10);
    if (known(d, fam, tier)) return true;
    a.dropped.push({ kind: 'discovery', what: key, refund: 0 });
    return false;
  });
  p.seen = p.seen.filter((id, i) => {
    if (d.shop.some((x) => x.id === id) && p.seen.indexOf(id) === i) return true;
    a.dropped.push({ kind: 'shop', what: id, refund: 0 });
    return false;
  });
  p.firstBuilt = p.firstBuilt.filter((t, i) => {
    if ((t === 'deposit' || t in d.buildings) && p.firstBuilt.indexOf(t) === i) return true;
    a.dropped.push({ kind: 'firstBuilt', what: t, refund: 0 });
    return false;
  });
  const goal = d.unlocks[p.goal];
  if (p.goal !== -1 && (goal === undefined || !p.unlocked.includes(goal.id))) {
    a.dropped.push({ kind: 'index', what: `goal ${p.goal}`, refund: 0 });
    p.goal = -1;
  }
  if (p.tutorial < 0 || p.tutorial > d.tutorial.length) {
    a.dropped.push({ kind: 'index', what: `tutorial ${p.tutorial}`, refund: 0 });
    p.tutorial = p.tutorial < 0 ? 0 : d.tutorial.length;
  }
}
